import { memo } from 'react';
import type { KeyboardEvent } from 'react';
import { Tooltip } from '../components/Tooltip';
import type { DataTableColumn } from './types';
import { cellClasses, cx, ROW_BORDER } from './utils';

const ROW_FOCUS =
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400';

/** `'button'`/`'click'` mirror `DataTableRowAction['mode']`; absent = inert row. */
export type DataTableRowActionMode = 'button' | 'click';

export interface DataTableBodyRowProps<T, K extends string> {
    row: T;
    /** The row's position in the current page - forwarded to `column.render`. */
    index: number;
    /** The already-filtered visible columns. Memoized by `DataTable`. */
    columns: ReadonlyArray<DataTableColumn<T, K>>;
    rowHeightClassName: string;
    /**
     * `rowClassName?.(row, index)`, pre-computed by `DataTable`. A string
     * rather than the caller's function on purpose: strings compare by value
     * under `memo`'s shallow check, so an inline `rowClassName` arrow (which
     * every current caller passes) can neither defeat the memo nor go stale -
     * when its output for this row changes, this prop changes with it.
     */
    extraClassName?: string;
    /** `rowAction.getLabel?.(row)`, pre-computed for the same reason. */
    actionLabel?: string;
    /** `rowAccent?.(row, index)`, pre-computed - a 2px strip in the first cell. */
    accentColor?: string;
    /** `rowAction`'s mode, or `undefined` for no row action at all. */
    actionMode?: DataTableRowActionMode;
    /**
     * Activates the row. `DataTable` passes a ref-backed `useCallback` that is
     * referentially stable for the component's whole life, so the inline
     * `rowAction={{ onActivate: ... }}` object every caller writes never
     * reaches the memo comparison.
     */
    onActivate: (row: T, index: number) => void;
}

/**
 * Interaction props for an actionable row. The `'button'` mode makes the
 * `<tr>` itself focusable and Enter/Space-activatable but deliberately does
 * NOT override its implicit `row` role: a `role="button"` `<tr>` stops being
 * a row to assistive tech, which orphans its `<td>`s from `A11yHeaderRow`'s
 * column headers and breaks the table's row count - dismantling, for exactly
 * the rows users interact with, the semantics the split-header architecture
 * in `DataTable.tsx` exists to preserve. `row` supports an author-provided
 * name, so `aria-label` stays here on the focused element. (The alternative -
 * an inner `role="button"` element - would need a cell of its own, which the
 * caller-defined column contract has no slot for.)
 */
function rowInteractionProps(
    mode: DataTableRowActionMode,
    actionLabel: string | undefined,
    activate: () => void
) {
    if (mode === 'click') {
        return { onClick: activate };
    }
    return {
        tabIndex: 0,
        'aria-label': actionLabel,
        onClick: activate,
        onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
            // Rows routinely nest their own controls in a cell that stops
            // click propagation. Without this guard, Enter on one of those
            // controls would bubble up and activate the row as well.
            if (event.target !== event.currentTarget) {
                return;
            }
            if (event.key !== 'Enter' && event.key !== ' ') {
                return;
            }
            event.preventDefault();
            activate();
        }
    };
}

/**
 * One body row, memoized. In `mode="infinite"` rows only accumulate, so on a
 * parent re-render that doesn't concern them (a keystroke in a co-located
 * search box, a filter pill opening) every accumulated row would otherwise
 * re-run every cell's `render`. The memo holds because every prop is either
 * compared by value (`index`, the class strings, `actionLabel`, `actionMode`)
 * or referentially stable across such renders: `row` comes from the caller's
 * fetched data (stable under TanStack Query), `columns` is `DataTable`'s
 * memoized visible-columns array over the caller's memoized `columns` prop,
 * and `onActivate` is `DataTable`'s ref-backed stable callback. Nothing here
 * receives a freshly-created object or closure per render.
 */
function BodyRow<T, K extends string>({
    row,
    index,
    columns,
    rowHeightClassName,
    extraClassName,
    actionLabel,
    accentColor,
    actionMode,
    onActivate
}: DataTableBodyRowProps<T, K>) {
    return (
        <tr
            {...(actionMode
                ? rowInteractionProps(actionMode, actionLabel, () => onActivate(row, index))
                : {})}
            className={cx(
                rowHeightClassName,
                'transition-colors',
                actionMode && 'cursor-pointer hover:bg-gray-50',
                actionMode === 'button' && ROW_FOCUS,
                extraClassName
            )}
        >
            {columns.map((column, columnIndex) => {
                const paintAccent = columnIndex === 0 && accentColor !== undefined;
                const cell = (
                    <td
                        key={column.key}
                        className={cx(cellClasses(column), ROW_BORDER, paintAccent && 'relative')}
                        onClick={
                            column.stopClickPropagation
                                ? (event) => event.stopPropagation()
                                : undefined
                        }
                        onKeyDown={
                            column.stopClickPropagation
                                ? (event) => event.stopPropagation()
                                : undefined
                        }
                    >
                        {paintAccent && (
                            <span
                                aria-hidden="true"
                                className="absolute inset-y-0 left-0 w-0.5"
                                style={{ background: accentColor }}
                            />
                        )}
                        {column.render(row, index)}
                    </td>
                );
                const title = column.getTitle?.(row);
                if (!title) {
                    return cell;
                }
                // The `<td>` ITSELF is the Radix trigger (`asChild` clones
                // it) - no wrapper element enters the table, so `truncate`
                // and the fixed column geometry are untouched, and the
                // content portals to <body>, out of reach of the scroll
                // region's `overflow-x: hidden`. Like the native `title`
                // this replaces, the reveal shows on hover whether or not
                // the text actually overflows. Cells hosting their own
                // controls keep their events: Radix composes the cloned
                // handlers, it never swallows them.
                return (
                    <Tooltip key={column.key} content={title}>
                        {cell}
                    </Tooltip>
                );
            })}
        </tr>
    );
}

// The cast restores the generic call signature `memo` erases; `memo`'s props
// contract is identical to the wrapped component's.
export const DataTableBodyRow = memo(BodyRow) as typeof BodyRow;
